import { useEffect, useState, ReactNode } from 'react'
import { trackThemeToggle } from '../lib/analytics'
import { CHROME_COLOUR, THEME_STORAGE_KEY, ThemeContext, Theme } from './theme-context-types'

interface ThemeProviderProps {
  children: ReactNode
}

/**
 * When to write color-scheme after a theme toggle.
 *
 * The site's colour transitions run 200ms (`transition-colors duration-200`), and
 * a color-scheme write forces a style recalc that RESTARTS whatever is still in
 * flight. Landing exactly on 200ms is a coin flip on a real device, so this clears
 * the fade with a margin. Too long and iOS holds the old canvas colour for longer
 * than it needs to; this is the smallest delay that reliably lands after.
 *
 * Exported so the test asserts against it rather than restating the number.
 */
export const COLOR_SCHEME_DELAY_MS = 250

/**
 * Module-level rather than a ref because its target is document.documentElement,
 * of which there is exactly one however many providers exist. Every write clears
 * the pending one first, so the last theme chosen wins even when an immediate
 * write overtakes a deferred one still in flight.
 */
let pendingColorScheme: ReturnType<typeof setTimeout> | undefined

/**
 * Drops a deferred write that has not landed yet. Without this, a provider that
 * unmounts mid-defer still writes to <html> 250ms later, over whatever owns it by
 * then. The app never unmounts its provider, but tests do, and the module-level
 * handle above means one leaked timer reaches every subsequent one.
 */
const cancelPendingColorScheme = () => {
  if (pendingColorScheme === undefined) return
  clearTimeout(pendingColorScheme)
  pendingColorScheme = undefined
}

/**
 * `color-scheme` tells the UA which scheme its OWN surfaces should use: scrollbars,
 * form controls, and the default it falls back to for anything the page has not
 * painted. It has to follow this site's theme.
 *
 * Being careful about what is established and what is not, because three files
 * used to assert three incompatible mechanisms for the same pixel. What was
 * actually observed on a physical iPhone, in order:
 *
 *   - Declared as `light dark`: both ends of the screen went near-black in LIGHT
 *     mode. That form resolves against the OS preference, and this theme is
 *     class-based and can legitimately disagree with it. Never use it here.
 *   - Not declared at all: dark mode showed white strips. (Measured on the Vite
 *     dev server, which paints no canvas at first paint, so this observation is
 *     confounded and proves less than it appears to.)
 *   - Declared per theme, as here: correct.
 *
 * What is NOT established is whether this or the html background is what colours
 * any given strip. The colours are kept consistent rather than the mechanism
 * being relied on. src/index.css owns the canvas and explains that end.
 *
 * It cannot be declared in CSS alongside the `dark` class: changing color-scheme
 * forces a style recalc that RESTARTS in-flight transitions, so the site's 200ms
 * colour fades kept starting over and text crawled toward its target for more
 * than a second. Applying it after the fade leaves the transitions alone.
 */
const applyColorScheme = (theme: Theme, defer: boolean) => {
  cancelPendingColorScheme()
  const set = () => {
    document.documentElement.style.colorScheme = theme
    pendingColorScheme = undefined
  }
  if (defer) pendingColorScheme = setTimeout(set, COLOR_SCHEME_DELAY_MS)
  else set()
}

/**
 * Applies a theme to the document. The class (which drives every `dark:` rule),
 * theme-color and color-scheme (which drive the browser's own chrome) have to
 * move together, so they live in one place rather than at each call site.
 *
 * `defer` holds color-scheme back past a colour transition already in flight;
 * pass it for a toggle, not for first load, where nothing is animating yet.
 */
const applyTheme = (theme: Theme, { defer = false } = {}) => {
  document.documentElement.classList.toggle('dark', theme === 'dark')
  applyColorScheme(theme, defer)

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = CHROME_COLOUR[theme]
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>('light')

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

    const initialTheme = savedTheme || systemPreference
    setTheme(initialTheme)
    applyTheme(initialTheme)

    return cancelPendingColorScheme
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    applyTheme(newTheme, { defer: true })

    // Track theme toggle event
    trackThemeToggle(newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

